/* eslint-disable array-callback-return */
// REACT IMPORTS
import React, { useEffect, useMemo, useState } from "react";
import AddressComp from "components/payments/AddressComp";

// COMPONENT IMPORTS
import { orderDetailsGet, paymentLogoGet } from "store/slices/paymentsSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import Skeleton from "react-loading-skeleton";
import { useUserData } from "store/slices/authSlice";
import {
  changePaymentScheduleName,
  formatCurrency,
  getPaymentMethod,
} from "utils/HelperFunctions";

// ICONS IMPORTS
import logo from "icons/smallLogo.svg";
import { CloseIcon } from "icons/payments/CloseIcon";
import { DownloadIcon } from "icons/payments/DownloadIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import { FilledInfoIcon } from "icons/payments/FilledInfoIcon";
// import { ExportIcon } from "icons/payments/ExportIcon";
import QuestionsFooter from "components/auth/QuestionsFooter";
import { GreenTrueIcon } from "icons/payments/GreenTrueIcon";
import { RedCrossIcon } from "icons/payments/RedCrossIcon";
import { MoneyReciveIcon } from "icons/payments/MoneyReciveIcon";
import { DollarProcessIcon } from "icons/payments/DollarProcessIcon";
import { InfoIcon } from "icons/payments/InfoIcon";
import { ClockYellowIcon } from "icons/payments/ClockYellowIcon";
import { CloseYellowIcon } from "icons/payments/CloseYellowIcon";
import CustomTooltip from "components/ui-component/CustomTooltip";
import { TickSquare } from "icons/payments/TickSquare";
import { DangerIcon } from "icons/payments/DangerIcon";
import { useParams } from "react-router-dom";
import { formatPaymentListDate } from "utils/DateFormat";
import { decode } from "utils/HelperFunctions";
import moment from "moment";
import { RootState } from "store/store";
import { usePortalSettingsData } from "store/slices/dashboardSlice";

// PROPS TYPE
interface PaymentPreviewProps {
  id: string;
  setId: any;
  show: boolean;
  setShow: any;
  onClose?: () => void;
  onDownloadCLick?: any;
  isScheduled?: boolean | false;
  isDownloading?: any | "";
  accountId?: any;
}
type Stats = {
  [key: string]: string[];
};

/* ------------------------------------- PAYMENT PREVIEW ------------------------------------- */

const PaymentPreview = (props: PaymentPreviewProps) => {
  const {
    id,
    setId,
    show,
    setShow,
    onClose: restorePreviousPreview,
    onDownloadCLick,
    isScheduled,
    isDownloading,
    accountId,
  } = props;

  const dispatch = useAppDispatch();
  const portalSettings = usePortalSettingsData();
  const { paymentLogo } = useAppSelector((state: RootState) => state.payment);
  const userData = useUserData();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [orderData, setOrderData] = useState<any>({});
  const [cppData, setCppData] = useState<any>({});
  const [scheduledDetails, setScheduledDetails] = useState<any>({});
  const [scheduled, setScheduled] = useState<any>([]);
  const [paymentStatus, setPaymentStatus] = useState<string>("");
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);

  const PaymentStatus: Stats = {
    "0": ["Failed", "Disputed", "Disputed Lost"],
    "1": ["Succeeded", "Disputed Won"],
    "2": ["Refunded", "Partial refund"],
    "3": ["Voided"],
    "4": ["Voided"],
    "5": ["Voided"],
    "7": ["Pending"],
  };

  const getStatusData = (
    payment_gateway_resp: string,
    transaction_status: string
  ): string => {
    if (payment_gateway_resp) {
      let stat = PaymentStatus[payment_gateway_resp];
      if (stat.length === 1) {
        return stat[0];
      } else {
        if (payment_gateway_resp === "0") {
          return transaction_status === "Disputed"
            ? "Disputed"
            : transaction_status === "DisputedLoss"
              ? "Disputed Lost"
              : "Failed";
        } else if (payment_gateway_resp === "1") {
          return transaction_status === "DisputedWon"
            ? "Disputed Won"
            : transaction_status === "Disputed"
              ? "Disputed"
              : "Succeeded";
        } else {
          return transaction_status === "RefundedPartial"
            ? "Partial refund"
            : "Refunded";
        }
      }
    }
    return "";
  };

  const getPayAmount = async (
    custom_sid: any,
    arr: any,
    pay_amount: any,
    cf_amount: any
  ) => {
    if (arr.length > 0) {
      const data = arr
        .filter((x: any) => x.status === "Pending")
        .find((x: any) => x.id === custom_sid);
      if (data) {
        return { pay: data?.amount, cf_amount: data?.cf_amount };
      }
    }
    return { pay: pay_amount, cf_amount: cf_amount };
  };

  const onGetPaymentReceipt = async () => {
    setIsLoading(true);
    try {
      let res: any = await dispatch(
        orderDetailsGet({
          custom_sid: id,
        })
      ).unwrap();
      if (res && res?.body?.data) {
        const { pay, cf_amount } = await getPayAmount(
          res?.body?.data?.custom_sid,
          res?.body?.data?.clientsScheduleCustomData,
          res?.body?.data?.amount,
          res?.body?.data?.totalconveFeeAmount
        );
        const subTotal = res?.body?.data?.clientsScheduleCustomData?.reduce(
          (acc: any, curr: any) =>
            curr?.amount ? acc + parseFloat(curr.amount) : acc,
          0
        );
        let data = {
          ...res?.body?.data,
          disputed_date: res?.body?.data?.disputed_date,
          pay_amount: res?.body?.data?.amount
            ? parseFloat(res?.body?.data?.amount)
            : 0,
          amount: pay ? parseFloat(pay) : 0,
          grandTotal: res?.body?.data?.grandTotal
            ? parseFloat(res?.body?.data?.grandTotal)
            : 0,
          subTotal,
          c_FeeAmount: res?.body?.data?.totalconveFeeAmount
            ? parseFloat(res?.body?.data?.totalconveFeeAmount)
            : null,
          cf_fee_amount: cf_amount ? parseFloat(cf_amount) : null,
        };
        let scheduledData = { ...res?.body?.data?.getCustomerData };
        let scheduledArr = [
          ...res?.body?.data?.clientsScheduleCustomData,
        ]?.filter((x) => x.status === "Pending");
        setOrderData({
          ...data,
        });
        setCppData({ ...data?.cppData });
        data?.payment_gateway_response &&
          setPaymentStatus(
            getStatusData(
              data.payment_gateway_response,
              data?.transaction_status
            )
          );
        setScheduledDetails({
          ...scheduledData,
        });
        setScheduled([...scheduledArr]);
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      console.log("Get payment receipt err: -->", error?.message);
    }
  };

  const onGetLogo = async () => {
    try {
      let payload = {
        cluster_id: clusterId,
      };
      await dispatch(paymentLogoGet(payload)).unwrap();
    } catch (error: any) {
      console.log("get logo err: -->", error?.message);
    }
  };

  useEffect(() => {
    if (!paymentLogo && clusterId && show) onGetLogo();
  }, [paymentLogo, clusterId]);

  useEffect(() => {
    if (!id && !show) return;
    onGetPaymentReceipt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const resetPreview = () => {
    setScheduled([]);
    setScheduledDetails({});
    setOrderData({});
    setPaymentStatus("");
  };

  useEffect(() => {
    if (!show) {
      resetPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const onClose = () => {
    setId("");
    setShow(false);
    resetPreview();
    restorePreviousPreview?.();
  };

  const isScheduledArray = useMemo(() => {
    if (orderData?.payment_method === "WTO") {
      if (paymentStatus !== "Succeeded" && !isLoading && scheduled?.length) {
        return true;
      } else {
        return false;
      }
    } else {
      if (scheduled?.length && !isLoading) {
        return true;
      } else {
        return false;
      }
    }
  }, [orderData, paymentStatus, scheduled, isLoading]);

  return (
    <div className="bg-bgSecondary h-[var(--shell-vh)] overflow-y-auto pt-[70px]">
      <div className="lg:px-10 px-5 py-5 bg-bgPrimary flex items-center justify-between fixed top-0 w-full z-[3]">
        <div className="flex items-center gap-2">
          <EyeIcon
            strokeColor="var(--color-txt-primary)"
            width="24"
            height="24"
          />
          <div className="lg:text-[26px] text-[22px] text-txtPrimary font-medium leading-[30px]">
            Preview
          </div>
        </div>
        <div className="flex items-center gap-5">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => {
              onDownloadCLick(orderData.id, orderData?.receiptId);
            }}
          >
            {isDownloading?.trim() !== "" ? (
              <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
            ) : (
              <DownloadIcon
                strokeColor="var(--color-btn-primary-active)"
                width="16"
                height="16"
              />
            )}
            <div className="text-sm font-medium text-btnPrimaryActive hover:!text-btnPrimaryHover leading-normal underline">
              {isDownloading?.trim() !== "" ? "Downloading..." : "Download"}
            </div>
          </div>
          <div
            onClick={() => onClose()}
            className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
          >
            <CloseIcon />
          </div>
        </div>
      </div>
      <div className="lg:px-10 px-5 py-4 flex items-center justify-between">
        <div className="md:text-xl text-lg font-normal leading-normal text-txtPrimary text-left w-full">
          Payment Receipt
        </div>
        <div className="flex items-center gap-[10px] text-right w-fit">
          {isLoading ? (
            <Skeleton width={170} height={50} />
          ) : paymentLogo ? (
            <div className="h-16 lg:h-16 flex justify-end">
              <img
                src={paymentLogo}
                alt=""
                className="animate__animated animate__fadeIn object-contain"
              />
            </div>
          ) : (
            <img
              src={logo}
              alt=""
              className="animate__animated animate__fadeIn"
            />
          )}
          {!paymentLogo && (
            <div className="text-purple font-medium text-xl leading-[30px]">
              CompanyName
            </div>
          )}
        </div>
      </div>
      <div className="lg:px-10 px-5 pb-2">
        <div className="lg:px-6 px-4 py-5 bg-bgPrimary border-x border-t border-strokePrimary rounded-t-lg">
          <div className="flex flex-col items-start gap-5">
            <div className="text-txtPrimary text-sm leading-normal font-bold uppercase">
              Billed to
            </div>
            <div className="h-[1px] w-full bg-strokePrimary" />
            <div className="flex flex-col items-start gap-4 w-full">
              <div className="flex items-center justify-between w-full">
                <div className="text-txtSecondary font-normal text-sm leading-normal">
                  Account ID
                </div>
                <div className="font-normal text-sm leading-normal text-txtPrimary text-right">
                  {isLoading ? (
                    <Skeleton width={80} />
                  ) : (
                    orderData?.accountId || accountId
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between w-full">
                <div className="font-normal text-sm leading-normal text-txtSecondary">
                  Customer
                </div>
                <div className="font-medium text-sm leading-normal text-txtPrimary text-right">
                  {isLoading ? (
                    <Skeleton width={150} />
                  ) : (
                    `${orderData?.first_name || ""} ${
                      orderData?.last_name || ""
                    }`
                  )}
                </div>
              </div>
            </div>

            <AddressComp
              orderData={scheduledDetails}
              cppData={cppData}
              isLoading={isLoading}
            />

            {/* <div className="h-[1px] w-full bg-strokePrimary" /> */}
          </div>
        </div>
        <div className="lg:px-6 px-4 pb-5 bg-bgPrimary border-x border-b border-strokePrimary rounded-b-lg">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
              Payment method
            </div>
            <div className="flex items-center gap-2 text-txtPrimary">
              {isLoading ? (
                <Skeleton width={150} />
              ) : (
                <>
                  {orderData?.payment_method
                    ? getPaymentMethod({
                        card_type: orderData?.card_type,
                        method: orderData?.payment_method,
                        digits: orderData?.card_last_4_digits,
                        account_number: orderData?.card_last_4_digits,
                        className: "!gap-[2px]",
                        textClassName: "!text-txtPrimary",
                        isHidden: true,
                        bank_name: orderData?.bank_name,
                        rowId: id,
                      })
                    : "--"}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="lg:px-10 px-5 py-2">
        <div className="lg:px-6 px-4 py-5 bg-bgPrimary border-x border-t border-strokePrimary rounded-t-lg">
          <div className="flex flex-col items-start gap-5">
            <div className="w-full flex justify-between items-center">
              <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                Payment description
              </div>
              {isLoading ? (
                <Skeleton width={120} height={20} />
              ) : !isScheduled ? (
                paymentStatus !== "" && (
                  <div className="w-fit ">
                    <span
                      className={`py-[3px] px-[10px] rounded-[6px] flex gap-[6px] items-center text-sm
                      ${
                        paymentStatus === "Succeeded" ||
                        paymentStatus === "Disputed Won"
                          ? "bg-[#23B3390D] text-green"
                          : paymentStatus === "Failed" ||
                              paymentStatus === "Disputed Lost"
                            ? "bg-[#FF00000D] text-red"
                            : paymentStatus === "Voided" ||
                                paymentStatus === "Pending"
                              ? "bg-[#FDB6000D] text-yellow"
                              : paymentStatus === "Refunded" ||
                                  paymentStatus === "Partial refund"
                                ? "bg-[#1678F20D] text-blue"
                                : paymentStatus === "Disputed"
                                  ? " bg-[#4F576A0D] text-iconBright"
                                  : ""
                      }
                    `}
                    >
                      {paymentStatus === "Succeeded" ||
                      paymentStatus === "Disputed Won" ? (
                        <GreenTrueIcon />
                      ) : paymentStatus === "Failed" ||
                        paymentStatus === "Disputed Lost" ? (
                        <RedCrossIcon />
                      ) : paymentStatus === "Refunded" ? (
                        <MoneyReciveIcon />
                      ) : paymentStatus === "Partial refund" ? (
                        <DollarProcessIcon />
                      ) : paymentStatus === "Disputed" ? (
                        <InfoIcon />
                      ) : paymentStatus === "Pending" ? (
                        <ClockYellowIcon />
                      ) : paymentStatus === "Voided" ? (
                        <CloseYellowIcon />
                      ) : paymentStatus === "Disputed Won" ? (
                        <TickSquare />
                      ) : paymentStatus === "Disputed Lost" ? (
                        <DangerIcon />
                      ) : (
                        <></>
                      )}
                      {paymentStatus === "Disputed Lost"
                        ? "Failed"
                        : paymentStatus === "Disputed Won"
                          ? "Succeeded"
                          : paymentStatus}
                    </span>
                  </div>
                )
              ) : (
                <div className="w-fit ">
                  <span
                    className={`py-[3px] px-[10px] rounded-[6px] flex gap-[6px] capitalize items-center text-sm bg-[#FDB6000D] text-yellow`}
                  >
                    <ClockYellowIcon />
                    Pending
                  </span>
                </div>
              )}
            </div>
            <div className="h-[1px] w-full bg-strokePrimary" />
            <div className="flex flex-col items-start gap-4 w-full">
              <div className="flex items-center justify-between w-full">
                <div className="font-normal text-sm leading-normal text-txtSecondary">
                  Item description
                </div>
                <div className="font-normal text-sm leading-normal text-txtPrimary text-right">
                  {isLoading ? (
                    <Skeleton width={80} />
                  ) : orderData?.ivr_type === "MANUALPAY" ? (
                    "Manual payment"
                  ) : orderData?.payment_method === "WTO" &&
                    scheduled?.[0]?.status === "Pending" ? (
                    "Pending Payment"
                  ) : (
                    orderData?.prodDescription || "Online Payment"
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between w-full">
                <div className="font-normal text-sm leading-normal text-txtSecondary">
                  Payment date
                </div>
                <div className="font-medium text-sm leading-normal text-txtPrimary text-right">
                  {isLoading ? (
                    <Skeleton width={150} />
                  ) : (
                    formatPaymentListDate(
                      orderData?.insert_date,
                      userData?.data?.accountData?.timezone,
                      "long"
                    )
                  )}
                </div>
              </div>
              {paymentStatus === "Succeeded" &&
                orderData?.recovered_payment === "Y" && (
                  <div className="flex items-center justify-between w-full">
                    <div className="font-normal text-sm leading-normal text-txtSecondary">
                      Recovered on
                    </div>

                    <div className="font-medium text-sm leading-normal text-txtPrimary text-right cursor-pointer">
                      {formatPaymentListDate(
                        orderData?.last_update,
                        orderData?.order_tz ||
                          userData?.data?.accountData?.timezone,
                        "long"
                      )}
                    </div>
                  </div>
                )}
              {paymentStatus === "Failed" && orderData?.error && (
                <div className="flex items-center justify-between w-full">
                  <div className="font-normal text-sm leading-normal text-txtSecondary">
                    Failed reason
                  </div>
                  <CustomTooltip
                    maxwidth="430px"
                    place="top"
                    id="failed-reason-tooltip"
                    content={`<div class="text-xs leading-[18px] font-normal">${orderData.error}</div>`}
                  >
                    <div
                      id="failed-reason-tooltip"
                      className="font-medium text-sm leading-normal text-txtPrimary text-right cursor-pointer"
                    >
                      {orderData.error.slice(0, 17) + "..."}
                    </div>
                  </CustomTooltip>
                </div>
              )}
              {(paymentStatus === "Refunded" ||
                paymentStatus === "Partial refund" ||
                paymentStatus === "Voided" ||
                paymentStatus === "Disputed") && (
                <div className="flex items-center justify-between w-full">
                  <div className="font-normal text-sm leading-normal text-txtSecondary">
                    {paymentStatus === "Voided"
                      ? "Voided on"
                      : paymentStatus === "Disputed"
                        ? "Disputed on"
                        : "Refunded on"}
                  </div>
                  <div className="font-medium text-sm leading-normal text-txtPrimary text-right">
                    {isLoading ? (
                      <Skeleton width={150} />
                    ) : (
                      formatPaymentListDate(
                        paymentStatus === "Voided"
                          ? orderData?.voidDate || orderData?.last_update
                          : paymentStatus === "Disputed"
                            ? orderData?.disputed_date
                            : orderData?.refundDate,
                        userData?.data?.accountData?.timezone,
                        "long"
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="h-[1px] w-full bg-strokePrimary" />
            <div className="flex flex-col items-start gap-4 w-full">
              <div className="flex items-center justify-between w-full">
                <div className="text-sm leading-normal text-txtPrimary font-medium whitespace-nowrap">
                  Amount
                </div>
                <div className="font-bold text-sm leading-[150%] text-txtPrimary text-right max-w-[130px]">
                  {isLoading ? (
                    <Skeleton width={80} />
                  ) : (
                    formatCurrency(orderData?.amount) || "$0.00"
                  )}
                </div>
              </div>
              {orderData?.cc_fee_amount != 0 ||
              orderData?.ach_fee_amount != 0 ? (
                <div className="flex items-center justify-between w-full">
                  <div className="text-sm leading-normal text-txtPrimary font-medium whitespace-nowrap">
                    {isLoading ? (
                      <Skeleton width={120} />
                    ) : (
                      <>
                        {orderData?.isStax
                          ? "Surcharge"
                          : portalSettings?.cc_fee_label
                            ? portalSettings?.cc_fee_label
                            : "Convenience fee"}
                      </>
                    )}
                  </div>
                  <div className="font-bold text-sm leading-[150%] text-txtPrimary text-right max-w-[130px]">
                    {isLoading ? (
                      <Skeleton width={80} />
                    ) : (
                      formatCurrency(
                        orderData?.payment_method === "ACH"
                          ? orderData?.ach_fee_amount ||
                              scheduledDetails?.ach_fee_amount
                          : orderData?.cc_fee_amount !== 0 ||
                              scheduledDetails?.cc_fee_amount !== 0
                            ? orderData?.cc_fee_amount ||
                              scheduledDetails?.cc_fee_amount
                            : 0
                      ) || "$0.00"
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="lg:px-6 px-4 py-5 bg-btnPrimaryActive border-x border-b border-strokePrimary rounded-b-lg flex flex-col gap-[10px]">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm leading-normal font-bold uppercase text-bgPrimary">
              Total Paid
            </div>
            <div className="flex items-center gap-2 text-bgPrimary font-bold">
              {isLoading ? (
                <Skeleton width={100} />
              ) : paymentStatus === "Failed" ? (
                "$0.00"
              ) : (
                formatCurrency(
                  orderData?.amount +
                    (orderData?.payment_method === "ACH"
                      ? orderData?.ach_fee_amount ||
                        scheduledDetails?.ach_fee_amount
                      : orderData?.cc_fee_amount !== 0 ||
                          scheduledDetails?.cc_fee_amount !== 0
                        ? orderData?.cc_fee_amount ||
                          scheduledDetails?.cc_fee_amount
                        : 0)
                ) || "$0.00"
              )}
            </div>
          </div>
          {!isLoading &&
          (paymentStatus === "Refunded" ||
            paymentStatus === "Partial refund" ||
            paymentStatus === "Voided" ||
            paymentStatus === "Disputed") ? (
            <div className="flex items-start justify-between font-bold uppercase text-bgprimary">
              <div className="text-sm leading-normal font-bold uppercase text-bgPrimary">
                {paymentStatus === "Voided"
                  ? "Voided"
                  : paymentStatus === "Disputed"
                    ? "Disputed"
                    : "Refunded"}
              </div>
              <div className="flex items-center gap-2 text-bgPrimary font-bold">
                {`(${formatCurrency(orderData?.refundAmount)})` || "($0.00)"}
              </div>
            </div>
          ) : null}
          {!isLoading &&
          (paymentStatus === "Refunded" ||
            paymentStatus === "Partial refund" ||
            paymentStatus === "Voided" ||
            paymentStatus === "Disputed") ? (
            <div className="flex items-start justify-between font-bold uppercase text-bgprimary">
              <div className="text-sm leading-normal font-bold uppercase text-bgPrimary">
                Adjusted total
              </div>
              <div className="flex items-center gap-2 text-bgPrimary font-bold">
                {formatCurrency(
                  orderData?.amount +
                    (orderData?.payment_method === "ACH"
                      ? orderData?.ach_fee_amount || scheduledDetails?.ach_fee_amount
                      : orderData?.cc_fee_amount !==0 || scheduledDetails?.cc_fee_amount !== 0
                        ? orderData?.cc_fee_amount  || scheduledDetails?.cc_fee_amount
                        : 0) -
                    orderData?.refundAmount
                ) || "$0.00"}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {isScheduledArray ? (
        <div className="lg:px-10 px-5 py-2 animate__animated animate__fadeIn">
          <div className="flex flex-col items-start gap-4 w-full">
            <div className="lg:px-6 px-4 py-5 bg-bgPrimary border border-strokePrimary rounded-lg w-full overflow-x-scroll">
              <div className="w-full flex flex-col items-start gap-5 overflow-x-scroll">
                <div className="flex items-center gap-x-2 justify-between w-full lg:flex-nowrap flex-wrap">
                  <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                    SCHEDULE
                  </div>
                  <div className="flex items-center gap-2.5 text-txtSecondary text-xs justify-end">
                    <p>
                      Frequency:{" "}
                      <span className="text-txtPrimary font-medium">
                        {changePaymentScheduleName(
                          scheduledDetails?.payment_schedule
                        )}
                      </span>
                    </p>
                    <p>|</p>
                    <p>
                      Remaining payments:{" "}
                      <span className="text-txtPrimary capitalize font-medium">
                        {scheduledDetails?.remaining_payments || "--"}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary  min-w-[384px]" />
                <div
                  className={`grid ${orderData?.c_FeeAmount ? "grid-cols-4" : "grid-cols-3"} items-center w-full min-w-[384px]`}
                >
                  <div
                    className={` text-sm text-txtParagraph leading-normal text-start`}
                  >
                    Payment Date
                  </div>
                  <div
                    className={` text-sm text-txtParagraph leading-normal text-center`}
                  >
                    Status
                  </div>
                  {orderData?.c_FeeAmount ? (
                    <div
                      className={` text-sm text-txtParagraph leading-normal text-center`}
                    >
                      {orderData?.isStax ? "Surcharge" : "Fee"}
                    </div>
                  ) : null}
                  <div
                    className={` text-sm text-txtParagraph leading-normal text-end`}
                  >
                    Amount
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary  min-w-[384px]" />
                {scheduled?.length
                  ? scheduled?.map((x: any, idx: number) => {
                      return (
                        <div
                          className={`grid ${orderData?.c_FeeAmount ? "grid-cols-4" : "grid-cols-3"} items-center w-full min-w-[384px]`}
                          key={idx}
                        >
                          <div
                            className={` text-sm text-txtPrimary leading-normal text-start flex gap-[6px] items-center`}
                          >
                            {x?.billing_date
                              ? moment(x?.billing_date).format("MM/DD/YYYY")
                              : x?.payment_date
                                ? moment(x?.payment_date).format("MM/DD/YYYY")
                                : "--"}
                          </div>
                          <div
                            className={` text-sm text-txtPrimary leading-normal flex justify-center gap-1.5`}
                          >
                            <div
                              className={`px-2 rounded-md py-[3px] w-max ${
                                x.status === "Pending"
                                  ? "bg-[#FDB6000C] text-yellow"
                                  : x.status === "Approved"
                                    ? "bg-[#1678F20D] text-btnPrimaryActive"
                                    : x.status === "Declined" ||
                                        x.status === "Cancelled" ||
                                        x.status?.toLowerCase() ===
                                          "disputed lost"
                                      ? "bg-[#FF00000D] text-red"
                                      : x.status === "Voided"
                                        ? "bg-[#FDB6000D] text-yellow"
                                        : x.status === "Succeeded" ||
                                            x.status?.toLowerCase() ===
                                              "disputed won"
                                          ? "bg-[#23B3390D] text-green"
                                          : x.status === "RefundedPartial" ||
                                              x.status === "Refunded"
                                            ? "bg-[#1678F20D] text-blue"
                                            : x.status === "Disputed"
                                              ? "bg-[#4F576A0D] text-iconBright"
                                              : ""
                              }`}
                            >
                              {x.status &&
                              (x.status === "Declined" ||
                                x.status === "Cancelled")
                                ? "Failed"
                                : x.status === "RefundedPartial"
                                  ? "Partial refund"
                                  : x?.status}
                            </div>
                          </div>
                          {orderData?.c_FeeAmount ? (
                            <div
                              className={`  text-sm text-txtPrimary leading-normal text-center`}
                            >
                              {x?.cf_amount
                                ? formatCurrency(x?.cf_amount)
                                : "$0.00"}
                            </div>
                          ) : null}
                          <div
                            className={`  text-sm text-txtPrimary leading-normal text-end`}
                          >
                            {x?.amount ? formatCurrency(x?.amount) : "$0.00"}
                          </div>
                        </div>
                      );
                    })
                  : null}
                <div className="h-[1px] w-full bg-strokePrimary  min-w-[384px]" />
                <div
                  className={`grid ${orderData?.c_FeeAmount ? "grid-cols-4" : "grid-cols-3"} items-center w-full min-w-[384px]`}
                >
                  <div
                    className={`w-full  text-xs leading-normal text-txtPrimary`}
                  >
                    SUBTOTAL
                  </div>
                  <div className={`w-full `}></div>
                  {orderData?.c_FeeAmount ? (
                    <div
                      className={`w-full min-w-[56px] text-center text-txtPrimary font-bold leading-normal`}
                    >
                      {formatCurrency(orderData?.c_FeeAmount) || "$0.00"}
                    </div>
                  ) : null}
                  <div
                    className={` text-end text-txtPrimary font-bold leading-normal`}
                  >
                    {formatCurrency(orderData?.subTotal) || "$0.00"}
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary min-w-[384px]" />
                <div className="flex items-center justify-between w-full min-w-[384px]">
                  <div className="text-sm leading-normal font-bold uppercase text-txtPrimary min-w-[152px]">
                    Total
                  </div>
                  <div className="text-lg leading-normal font-bold text-txtPrimary  min-w-[192px] text-end">
                    {formatCurrency(orderData?.grandTotal) || "$0.00"}
                  </div>
                </div>
              </div>
            </div>
            {orderData?.payment_method !== "PP" && (
              <div className="flex items-center gap-2 text-xs font-normal leading-normal text-txtParagraph ">
                <FilledInfoIcon />
                <p>
                  A{" "}
                  <span className="lowercase">
                    {portalSettings?.cc_fee_label
                      ? portalSettings?.cc_fee_label
                      : "convenience fee"}
                  </span>{" "}
                  is added to each installment payment.
                </p>
              </div>
            )}
          </div>
        </div>
      ) : null}
      {!isLoading && (
        <div className={`py-5 lg:px-10 px-5 bottom-0 flex justify-center `}>
          <QuestionsFooter isPreview={true} />
        </div>
      )}
    </div>
  );
};

export default PaymentPreview;
